import React, { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { toast } from "sonner";

export const ReviewSection = ({ slug = null, title = "Customer Reviews" }) => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchReviews = () => {
    api.get("/reviews", { params: slug ? { slug } : {} }).then((r) => setReviews(r.data));
  };
  useEffect(fetchReviews, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.comment.trim()) {
      toast.error("Please fill your name and review");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", { ...form, slug });
      toast.success("Thank you! Your review has been posted.");
      setForm({ name: "", rating: 5, comment: "" });
      fetchReviews();
    } catch {
      toast.error("Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3">
        <h3 className="font-display text-2xl font-bold text-[#1F130B] mb-5">{title}</h3>
        {reviews.length === 0 ? (
          <p className="text-[#6B5B52] text-sm">No reviews yet. Be the first to review!</p>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => (
              <div key={r.id} className="rounded-2xl bg-white border border-[#E7E0D6] p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#FEF3C7] grid place-items-center font-bold text-[#9A3412]">
                      {r.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-[#1F130B] text-sm">{r.name}</p>
                      <StarRating value={r.rating} />
                    </div>
                  </div>
                  <span className="text-xs text-[#9CA3AF]">{new Date(r.created_at).toLocaleDateString("en-IN")}</span>
                </div>
                <p className="mt-3 text-sm text-[#6B5B52] leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="lg:col-span-2">
        <form onSubmit={submit} className="rounded-2xl bg-white border border-[#E7E0D6] p-6 sticky top-28" data-testid="review-form">
          <h4 className="font-display text-xl font-bold text-[#1F130B]">Write a Review</h4>
          <p className="text-sm text-[#6B5B52] mt-1">Share your experience with us</p>
          <div className="mt-4 space-y-3">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              data-testid="review-name-input"
              className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none focus:border-[#C2410C]"
            />
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setForm({ ...form, rating: i })}
                  data-testid={`review-star-${i}`}
                >
                  <Star size={26} className={i <= form.rating ? "fill-[#D97706] text-[#D97706]" : "text-[#D6C9BA]"} />
                </button>
              ))}
            </div>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Tell us what you loved..."
              rows={4}
              data-testid="review-comment-input"
              className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none focus:border-[#C2410C] resize-none"
            />
            <button
              disabled={submitting}
              data-testid="review-submit-btn"
              className="w-full rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold py-2.5 disabled:opacity-60"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
